<script setup lang="ts">
import { ArrowRight, Building2, Code2, Monitor } from 'lucide-vue-next'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from '../../../../base/src/composables/useI18n'
import { useSiteStore } from '../../../../base/src/stores/site'
import { useAuthStore } from '../../../../base/src/stores/auth'
import en from '../../locales/en'
import zh from '../../locales/zh'

const router = useRouter()
const { t } = useI18n({ zh, en })
const siteStore = useSiteStore()
const auth = useAuthStore()

const ways = computed(() => [
  {
    key: 'web',
    icon: Monitor,
    title: t('muse.intro.ways.webTitle'),
    desc: t('muse.intro.ways.webDesc'),
    to: auth.isAuthenticated ? '/user/muse/dashboard' : '/register',
  },
  {
    key: 'api',
    icon: Code2,
    title: t('muse.intro.ways.apiTitle'),
    desc: t('muse.intro.ways.apiDesc'),
    to: auth.isAuthenticated ? '/user/muse/avatarGenerate' : '/login',
  },
  {
    key: 'custom',
    icon: Building2,
    title: t('muse.intro.ways.customTitle'),
    desc: t('muse.intro.ways.customDesc'),
    to: auth.isAuthenticated ? '/user/muse/dashboard' : '/login',
  },
])
</script>

<template>
  <section id="ways" class="scroll-mt-20 py-12 sm:py-16">
    <div class="mx-auto max-w-6xl px-5 sm:px-8">
      <div class="mx-auto max-w-2xl text-center">
        <div class="text-2xl font-black tracking-tight sm:text-3xl">{{ t('muse.intro.ways.title') }}</div>
        <div class="mt-3 text-sm text-gray-500 sm:text-base dark:text-gray-400">
          {{ t('muse.intro.ways.subtitle', { name: siteStore.displayName }) }}
        </div>
      </div>

      <div class="mt-9 grid gap-4 sm:mt-12 sm:grid-cols-3 sm:gap-5">
        <div
          v-for="(way, index) in ways"
          :key="way.key"
          v-reveal
          :style="{ transitionDelay: `${index * 80}ms` }"
        >
          <div
            class="group h-full cursor-pointer rounded-2xl border border-gray-100 bg-panel/50 p-5 transition-all hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg sm:p-6 dark:border-gray-800 dark:bg-panel/30 dark:hover:border-primary-800"
            role="button"
            tabindex="0"
            @click="router.push(way.to)"
            @keydown.enter="router.push(way.to)"
          >
            <div
              class="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary-100/80 bg-primary-50 text-primary dark:border-primary-800/60 dark:bg-primary-900/30"
            >
              <component :is="way.icon" class="h-5 w-5" aria-hidden="true" />
            </div>
            <div class="mt-4 text-base font-semibold">{{ way.title }}</div>
            <div class="mt-1.5 text-xs leading-relaxed text-gray-500 sm:text-sm dark:text-gray-400">
              {{ way.desc }}
            </div>
            <div class="mt-4 inline-flex items-center gap-1 text-xs font-medium text-primary">
              {{ t('muse.intro.ways.action') }}
              <ArrowRight class="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
